import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Icon,
  InputAdornment,
  Chip,
  alpha,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { IESDoc, FilterGroup, TableColumn } from "../../../../app.interface";
import { staffService } from "../../services/staff.service";
import { EntityDetailsDrawer } from "../../components/EntityDetailsDrawer/EntityDetailsDrawer";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { AppSearch } from "../../../../components/AppSearch";
import { AppTable } from "../../../../components/AppTable";

interface StaffMember extends IESDoc {
  name: string;
  email: string;
  role: string;
  phone: string;
  status: "active" | "inactive";
  is_pos_user: boolean;
  permissions?: string[];
  branch?: string;
  department?: string;
  warehouse?: string;
  created_at: string;
}

interface Filter {
  id: string;
  title: string;
  field: string;
  operator: string;
  value: any;
}

export const StaffManager: React.FC = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = React.useState<StaffMember[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Filter[]>([]);
  const [startDate, setStartDate] = React.useState<number | undefined>();
  const [endDate, setEndDate] = React.useState<number | undefined>();
  const [dateField] = React.useState<string>("created_at");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedStaff, setSelectedStaff] = React.useState<StaffMember | null>(
    null
  );
  const [deleting, setDeleting] = React.useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);
  const [selectedStaffForDetails, setSelectedStaffForDetails] =
    React.useState<StaffMember | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select" as const,
      icon: "fiber_manual_record",
      description: "Filter staff by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
          description: "Currently active staff members",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
          description: "Inactive staff members",
        },
      ],
    },
    {
      id: "role",
      label: "Role",
      type: "select" as const,
      icon: "badge",
      description: "Filter staff by their role",
      options: [
        {
          id: "admin",
          label: "Admin",
          value: "admin",
          icon: "admin_panel_settings",
          color: "#0891B2",
        },
        {
          id: "manager",
          label: "Manager",
          value: "manager",
          icon: "manage_accounts",
          color: "#0891B2",
        },
        {
          id: "cashier",
          label: "Cashier",
          value: "cashier",
          icon: "point_of_sale",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select" as const,
      icon: "sort",
      description: "Sort staff by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  React.useEffect(() => {
    loadStaff();
  }, [
    page,
    pageSize,
    sortField,
    sortOrder,
    search,
    filters,
    startDate,
    endDate,
  ]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await staffService.listStaffMembers({
        searchQuery: search,
        filters,
        startDate,
        endDate,
        dateField,
        pageIndex: page,
        pageSize,
        sortField,
        sortOrder,
      });

      const staffData = (response.data || []) as StaffMember[];
      setStaff(staffData);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load staff members");
      console.error("Error loading staff:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (staff: IESDoc) => {
    navigate(`/business/configuration/staff/${staff._id}/edit`);
  };

  const handleOpenDeleteDialog = (staff: IESDoc) => {
    setSelectedStaff(staff as StaffMember);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedStaff(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteStaff = async () => {
    if (!selectedStaff) return;
    try {
      setDeleting(true);
      await staffService.deleteStaffMember(selectedStaff._id);
      toast.success("Staff member deleted successfully");
      handleCloseDeleteDialog();
      loadStaff();
    } catch (error) {
      toast.error("Failed to delete staff member");
      console.error("Error deleting staff:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDetailsOpen = (staff: IESDoc) => {
    setSelectedStaffForDetails(staff as StaffMember);
    setDetailsDrawerOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedStaffForDetails(null);
    setDetailsDrawerOpen(false);
  };

  const handleSort = (field: string, order: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(order);
  };

  const getStatusFromStaff = (staff: any) => {
    // Check is_active first
    if (typeof staff.is_active === "boolean") {
      return staff.is_active ? "active" : "inactive";
    }
    // Fallback to is_deleted if is_active is not present
    if (typeof staff.is_deleted === "boolean") {
      return staff.is_deleted ? "inactive" : "active";
    }
    // If neither field exists, default to active
    return "active";
  };

  const MasterColumns: TableColumn[] = [
    {
      id: "staffId",
      header: "Staff ID",
      field: "staffId",
      type: "action",
      width: "180px",
      renderAction: (row) => {
        return (
          <Fragment>
            <Typography fontWeight={600}>
              {`S-${row._id.substring(0, 8).toUpperCase()}`}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      id: "name",
      header: "Staff Name",
      field: "first_name",
      filterField: "first_name.keyword",
      type: "action",
      sortable: true,
      width: "200px",
      renderAction: (row) => {
        return (
          <Typography fontWeight={600}>
            {row.first_name} {row.last_name}
          </Typography>
        );
      },
    },
    {
      id: "email",
      header: "Email",
      field: "email",
      type: "text",
      width: "250px",
    },
    {
      id: "role",
      header: "Role",
      field: "role",
      type: "action",
      width: "150px",
      renderAction: (row) => (
        <Chip
          label={row.role}
          size="small"
          icon={<Icon sx={{ fontSize: 16 }}>badge</Icon>}
          variant="outlined"
          sx={{
            borderColor: alpha("#0891B2", 0.3),
            color: "#0891B2",
          }}
        />
      ),
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      filterField: "is_active",
      type: "status",
      width: "120px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row) => (
        <div className="flex gap-2 justify-end">
          <Tooltip title="Edit Staff" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Staff" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];
  console.log("Staff");
  console.log(staff);
  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Staff Management"
        subtitle="Manage your organization's staff members"
        icon="people"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => navigate("/business/configuration/staff/new")}
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Staff Member
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search staff by name, email, role, or branch..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={(newFilters) => setFilters(newFilters as Filter[])}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={`${totalItems} Staff Members`}
              color="primary"
              variant="outlined"
              icon={<Icon>people</Icon>}
              sx={{ borderColor: alpha("#0891B2", 0.3), color: "#0891B2" }}
            />
            <Chip
              label={`${
                staff.filter((s) => s.is_active == true).length
              } Active`}
              color="success"
              variant="outlined"
              icon={<Icon>check_circle</Icon>}
            />
            <Chip
              label={`${staff.filter((s) => s.is_pos_user).length} POS Users`}
              color="info"
              variant="outlined"
              icon={<Icon>point_of_sale</Icon>}
              sx={{ borderColor: alpha("#0891B2", 0.3), color: "#0891B2" }}
            />
          </Box>
        </Box>

        {loading && staff.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptyState
              icon="people"
              title="Loading Staff"
              description="Please wait while we fetch staff members..."
              height={300}
              loading
            />
          </Box>
        ) : staff.length === 0 ? (
          <EmptyState
            icon="people"
            title="No Staff Members Found"
            description={
              search
                ? "No staff members match your search criteria. Try adjusting your search term."
                : "Get started by adding your first staff member to manage access to your business."
            }
            actionText="Add Staff Member"
            onAction={() => navigate("/business/configuration/staff/new")}
            height={300}
          />
        ) : (
          <AppTable
            data={staff.map((member) => ({
              ...member,
              status: getStatusFromStaff(member),
            }))}
            allowHeaderWrap
            columns={MasterColumns}
            variant="default"
            sortConfig={{ field: sortField, direction: sortOrder }}
            onSort={handleSort}
            loading={loading}
            header={{
              title: "Staff List",
              subtitle: "Your current list of staff members",
            }}
            selectable
            onSelectionChange={(selected) => {
              console.log("Selected:", selected);
            }}
            styleOptions={{
              bordered: true,
              columnSeparators: true,
              headerBackground: alpha("#0891B2", 0.02),
              rowHoverBackground: alpha("#0891B2", 0.04),
              borderColor: alpha("#0891B2", 0.1),
            }}
            toolbarActions={{
              refresh: {
                label: "Refresh List",
                is_disabled: loading,
                onClick: loadStaff,
              },
              export: {
                label: "Export Staff",
                is_disabled: loading,
                options: {
                  formats: ["csv", "excel", "json"],
                  filename: "staff-list",
                },
              },
            }}
            pagination={{
              total: totalItems,
              page: page,
              pageSize: pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
            onRowClick={handleDetailsOpen}
            rowProps={{
              sx: {
                cursor: "pointer",
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.04),
                },
              },
            }}
          />
        )}
      </Paper>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteStaff}
        title="Delete Staff Member"
        itemName={selectedStaff?.name || ""}
        itemDescription={selectedStaff?.email || ""}
        loading={deleting}
        icon="person"
      />

      <EntityDetailsDrawer
        open={detailsDrawerOpen}
        onClose={handleDetailsClose}
        entity={selectedStaffForDetails}
        entityType="staff"
      />
    </Box>
  );
};
